import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, Search, Loader2, Plus, Check, Film, Tv } from 'lucide-react';
import { useLists, type MediaFilter } from '../contexts/ListsContext';
import { useUserLibrary, type MediaEnrichment } from '../contexts/UserLibraryContext';
import { useAuth } from '../contexts/AuthContext';
import { effectiveTier } from '../utils/entitlements';
import { UpgradeCTA } from './ui/UpgradeCTA';
import { SocialUpgradeModal } from './ui/SocialUpgradeModal';
import { ApiError } from '../lib/api';
import { calculateOurRating } from '../utils/helpers';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const IMAGE_BASE = 'https://image.tmdb.org/t/p/w92';

interface AddMovieModalProps {
  isOpen: boolean;
  onClose: () => void;
  watchlistId: string;
  watchlistName: string;
  mediaFilter?: MediaFilter;
  existingMediaIds?: Set<string>;
  isDefaultList?: boolean;
  listSlug?: 'watched' | 'watchlist' | 'favorites' | null;
  onRefetchNeeded?: () => void;
}

// TASK 3: SearchResult now includes enrichment fields from Meilisearch
// These are passed to toggle functions for instant metadata on WatchlistPage
interface SearchResult {
  id: number;
  title?: string;
  name?: string;
  poster_path?: string;
  release_date?: string;
  first_air_date?: string;
  media_type: 'movie' | 'tv';
  vote_average?: number;
  // Enrichment fields from Meilisearch
  genre_ids?: number[];
  runtime?: number;
  imdb_rating?: number;
  rt_rating?: number;
  filmpl_rating?: number;
  popularity?: number;
  original_language?: string;
  vote_count?: number;
}

export function AddMovieModal({
  isOpen,
  onClose,
  watchlistId,
  watchlistName,
  mediaFilter = 'mixed',
  existingMediaIds,
  isDefaultList,
  listSlug,
  // TASK 3: onRefetchNeeded no longer needed - enrichment passed optimistically
  onRefetchNeeded: _onRefetchNeeded,
}: AddMovieModalProps) {
  const { lists, toggleListItem, isInList } = useLists();
  const {
    toggleWatched,
    toggleWatchlist,
    toggleFavorites,
    isWatched,
    isInWatchlist,
    isInFavorites,
  } = useUserLibrary();
  const { profile } = useAuth();
  const isFree = !!profile && effectiveTier(profile) === 'free';
  const [upgradeModalFeature, setUpgradeModalFeature] = useState<string | null>(null);

  // Determine list type from props or watchlists fallback
  const listInfo = useMemo(() => {
    if (isDefaultList !== undefined && listSlug !== undefined) {
      return { isDefault: isDefaultList, slug: listSlug };
    }
    const list = lists.find((w) => w.id === watchlistId);
    return {
      isDefault: list?.is_default ?? false,
      slug: (list?.slug as 'watched' | 'watchlist' | 'favorites' | null) ?? null,
    };
  }, [isDefaultList, listSlug, lists, watchlistId]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [addingIds, setAddingIds] = useState<Set<string>>(new Set());
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Text labels based on media filter
  const labels = {
    title: mediaFilter === 'movie' ? 'Add Movies' : mediaFilter === 'tv' ? 'Add TV Shows' : 'Add Movies & Shows',
    placeholder: mediaFilter === 'movie' ? 'Search movies...' : mediaFilter === 'tv' ? 'Search TV shows...' : 'Search movies and TV shows...',
    emptyState: mediaFilter === 'movie' ? 'Start typing to search for movies' : mediaFilter === 'tv' ? 'Start typing to search for TV shows' : 'Start typing to search for movies and TV shows',
  };

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setAddedIds(new Set());
      setHasSearched(false);
    }
  }, [isOpen]);

  // Debounced search
  const search = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      // Search based on media filter
      const searchMovies = mediaFilter === 'movie' || mediaFilter === 'mixed';
      const searchTV = mediaFilter === 'tv' || mediaFilter === 'mixed';

      const promises: Promise<Response>[] = [];
      if (searchMovies) {
        promises.push(fetch(`${API_BASE_URL}/search/movie?query=${encodeURIComponent(searchQuery)}&limit=15`));
      }
      if (searchTV) {
        promises.push(fetch(`${API_BASE_URL}/search/tv?query=${encodeURIComponent(searchQuery)}&limit=15`));
      }

      const responses = await Promise.all(promises);
      const data = await Promise.all(responses.map(r => r.json()));

      let combined: SearchResult[] = [];

      if (mediaFilter === 'movie') {
        // Movies only
        combined = (data[0].results || []).map((m: SearchResult) => ({ ...m, media_type: 'movie' as const })).slice(0, 15);
      } else if (mediaFilter === 'tv') {
        // TV only
        combined = (data[0].results || []).map((t: SearchResult) => ({ ...t, media_type: 'tv' as const })).slice(0, 15);
      } else {
        // Mixed - interleave results
        const movies = (data[0].results || []).map((m: SearchResult) => ({ ...m, media_type: 'movie' as const }));
        const tvShows = (data[1].results || []).map((t: SearchResult) => ({ ...t, media_type: 'tv' as const }));

        const maxLength = Math.max(movies.length, tvShows.length);
        for (let i = 0; i < maxLength && combined.length < 15; i++) {
          if (i < movies.length && combined.length < 15) combined.push(movies[i]);
          if (i < tvShows.length && combined.length < 15) combined.push(tvShows[i]);
        }
      }

      setResults(combined);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
      setHasSearched(true);
    }
  }, [mediaFilter]);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      search(value);
    }, 300);
  };

  // Handle adding a movie/show to the list
  const handleAdd = async (item: SearchResult) => {
    const uniqueId = `${item.media_type}-${item.id}`;
    if (addingIds.has(uniqueId) || addedIds.has(uniqueId)) return;

    setAddingIds((prev) => new Set(prev).add(uniqueId));

    try {
      const title = item.title || item.name || 'Unknown';
      const year = item.release_date || item.first_air_date;
      const yearNum = year ? parseInt(year.slice(0, 4), 10) : undefined;
      const poster = item.poster_path ?? undefined;

      // TASK 3: Build enrichment from search result for instant metadata display
      // Calculate filmpl_rating if not provided (Meilisearch should include it, but fallback)
      const filmplRating = item.filmpl_rating ?? calculateOurRating(item.imdb_rating, item.rt_rating);
      const enrichment: MediaEnrichment = {
        genre_ids: item.genre_ids,
        runtime: item.runtime,
        imdb_rating: item.imdb_rating,
        rt_rating: item.rt_rating,
        filmpl_rating: filmplRating ?? undefined,
        popularity: item.popularity,
        original_language: item.original_language,
        vote_count: item.vote_count,
      };

      if (listInfo.isDefault && listInfo.slug) {
        // ----- DEFAULT LIST: Use UserLibraryContext toggles -----

        // Check if already in list (guard against double-add)
        let alreadyInList = false;
        switch (listInfo.slug) {
          case 'watched':
            alreadyInList = isWatched(item.id, item.media_type);
            break;
          case 'watchlist':
            alreadyInList = isInWatchlist(item.id, item.media_type);
            break;
          case 'favorites':
            alreadyInList = isInFavorites(item.id, item.media_type);
            break;
        }

        if (alreadyInList) {
          setAddedIds((prev) => new Set(prev).add(uniqueId));
          setAddingIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(uniqueId);
            return newSet;
          });
          return;
        }

        // TASK 3: Pass enrichment to toggles for instant WatchlistPage metadata
        // Note: toggles throw on failure, return null if superseded
        switch (listInfo.slug) {
          case 'watched':
            await toggleWatched(item.id, item.media_type, title, poster, yearNum, enrichment);
            break;
          case 'watchlist':
            await toggleWatchlist(item.id, item.media_type, title, poster, yearNum, enrichment);
            break;
          case 'favorites':
            await toggleFavorites(item.id, item.media_type, title, poster, yearNum, enrichment);
            break;
        }
        // If we get here, toggle succeeded (or was superseded - either way, treat as success)
        // NOTE: Removed onRefetchNeeded call - enrichment is now passed optimistically

      } else {
        // ----- CUSTOM LIST: Use ListsContext toggleListItem for optimistic item_count update -----

        // Guard: if already in list, mark as added and return (never toggle off)
        if (isInList(watchlistId, item.id, item.media_type)) {
          setAddedIds((prev) => new Set(prev).add(uniqueId));
          setAddingIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(uniqueId);
            return newSet;
          });
          return;
        }

        // TASK 3: Call toggleListItem with enrichment for instant metadata display
        // Mutation handles optimistic updates and cache writes - no need to track itemId here
        await toggleListItem(watchlistId, item.id, item.media_type, title, poster, yearNum, undefined, undefined, enrichment);
      }

      setAddedIds((prev) => new Set(prev).add(uniqueId));
      // NOTE: onItemAdded removed - mutation is the single source of truth for cache writes

    } catch (err) {
      console.error('Error adding to list:', err);
      if (err instanceof ApiError && err.code === 'SOCIAL_TIER_REQUIRED') {
        setUpgradeModalFeature('Adding to custom lists');
      }
    } finally {
      setAddingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(uniqueId);
        return newSet;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0A0A0A]/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[#0F1419] rounded-xl ring-1 ring-white/[0.06] w-full max-w-lg shadow-2xl shadow-black/50 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-lg font-semibold text-white">{labels.title}</h2>
            <p className="text-sm text-slate-400">to {watchlistName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-[#1A1F26] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Defense-in-depth: Free users on custom lists see upgrade CTA */}
        {isFree && !listInfo.isDefault ? (
          <div className="p-4">
            <UpgradeCTA feature="Adding to custom lists" onNavigate={onClose} />
          </div>
        ) : (
        <>
        {/* Search Input */}
        <div className="p-4 border-b border-white/[0.06]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              placeholder={labels.placeholder}
              className="w-full pl-10 pr-4 py-3 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 animate-spin" />
            )}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {results.length === 0 && query && !loading && hasSearched && (
            <div className="text-center py-8 text-slate-500">
              No results found for "{query}"
            </div>
          )}

          {results.length === 0 && !query && (
            <div className="text-center py-8 text-slate-500">
              {labels.emptyState}
            </div>
          )}

          <div className="space-y-2">
            {results.map((item) => {
              const uniqueId = `${item.media_type}-${item.id}`;
              const isAdding = addingIds.has(uniqueId);
              const isAdded = addedIds.has(uniqueId) || existingMediaIds?.has(uniqueId);
              const title = item.title || item.name || 'Unknown';
              const year = item.release_date || item.first_air_date;

              return (
                <div
                  key={uniqueId}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#1A1F26] transition-colors"
                >
                  {/* Poster */}
                  <div className="w-12 h-18 bg-[#1A1F26] rounded flex-shrink-0 overflow-hidden">
                    {item.poster_path ? (
                      <img
                        src={`${IMAGE_BASE}${item.poster_path}`}
                        alt={title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-600">
                        {item.media_type === 'movie' ? <Film size={20} /> : <Tv size={20} />}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{title}</p>
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        item.media_type === 'movie' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        {item.media_type === 'movie' ? 'Movie' : 'TV'}
                      </span>
                      {year && <span>{year.slice(0, 4)}</span>}
                      {item.vote_average != null && item.vote_average >= 0.5 && (
                        <span>★ {item.vote_average.toFixed(1)}</span>
                      )}
                    </div>
                  </div>

                  {/* Add Button */}
                  <button
                    onClick={() => handleAdd(item)}
                    disabled={isAdding || isAdded}
                    className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                      isAdded
                        ? 'bg-teal-500/20 text-teal-400'
                        : isAdding
                        ? 'bg-slate-700 text-slate-400'
                        : 'bg-slate-700 text-white hover:bg-teal-500'
                    }`}
                  >
                    {isAdded ? (
                      <Check size={18} />
                    ) : isAdding ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Plus size={18} />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/[0.06]">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors font-medium"
          >
            Done
          </button>
        </div>
        </>
        )}
      </div>

      <SocialUpgradeModal
        isOpen={!!upgradeModalFeature}
        onClose={() => setUpgradeModalFeature(null)}
        feature={upgradeModalFeature || ''}
      />
    </div>
  );
}
